import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Shield, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Permission { id: string; code: string; displayName: string; description: string | null; isDangerous: boolean }
interface PermissionCategory { category: string; permissions: Permission[] }
interface RoleDefaults { roles: { role: string; permissions: string[] }[] }

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin', admin: 'Admin', accountant: 'Accountant',
  fleet_officer: 'Operations Manager', site_manager: 'Site Manager',
  construction_supervisor: 'Site Supervisor', driver: 'Driver',
  investor: 'Investor', auditor: 'Viewer',
  tv_display: 'TV Wall Display',
};

type View = 'by-permission' | 'by-role';

export default function PermissionsExplorerPage() {
  const [view, setView] = useState<View>('by-permission');
  const [cats, setCats] = useState<PermissionCategory[]>([]);
  const [roleMap, setRoleMap] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, rd] = await Promise.all([
        apiClient.get<{ categories: PermissionCategory[] }>('/api/v1/permissions'),
        apiClient.get<RoleDefaults>('/api/v1/permissions/role-defaults'),
      ]);
      setCats(p.data.categories);
      const m: Record<string, Set<string>> = {};
      for (const r of rd.data.roles) m[r.role] = new Set(r.permissions);
      setRoleMap(m);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allRoles = Object.keys(ROLE_LABELS);
  const totalPerms = cats.reduce((s, c) => s + c.permissions.length, 0);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Shield className="h-4 w-4" /> Permissions explorer</div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        <div className="text-xs text-muted-foreground">
          Read-only view of the permission system. {totalPerms} permissions across {cats.length} categories,
          mapped to {Object.keys(roleMap).length} roles. To grant or revoke individual permissions for a specific user,
          go to <Link to="/dashboard/settings/users" className="underline">Users &amp; permissions</Link>.
        </div>

        <div className="flex gap-2 border-b border-border">
          {(['by-permission', 'by-role'] as const).map(v => (
            <button key={v} onClick={() => setView(v)}
              className={'px-3 py-2 text-sm border-b-2 transition-colors -mb-px ' + (view === v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {v === 'by-permission' ? 'By permission' : 'By role'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>
        ) : view === 'by-permission' ? (
          <div className="space-y-4">
            {cats.map(cat => (
              <details key={cat.category} className="rounded-lg border border-border" open>
                <summary className="cursor-pointer px-4 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/50">
                  {cat.category} <span className="text-xs text-muted-foreground">({cat.permissions.length})</span>
                </summary>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-xs text-muted-foreground bg-muted/20 border-b border-border">
                      <tr>
                        <th className="text-left px-3 py-2 sticky start-0 bg-muted/20 z-10">Permission</th>
                        {allRoles.map(r => (
                          <th key={r} className="text-center px-2 py-2" style={{ minWidth: '64px' }}>
                            <div className="text-[10px] font-mono">{r.split('_').map(w => w[0]).join('').toUpperCase()}</div>
                            <div className="text-[9px] text-muted-foreground font-normal mt-0.5">{ROLE_LABELS[r]?.split(' ')[0] ?? r}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cat.permissions.map(p => (
                        <tr key={p.id} className="border-b border-border last:border-b-0 hover:bg-muted/10">
                          <td className="px-3 py-2 sticky start-0 bg-card">
                            <div className="text-sm flex items-center gap-2">
                              {p.displayName}
                              {p.isDangerous && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-700 dark:text-rose-400">danger</span>}
                            </div>
                            <div className="text-[10px] font-mono text-muted-foreground">{p.code}</div>
                          </td>
                          {allRoles.map(r => (
                            <td key={r} className="text-center px-2 py-2">
                              {roleMap[r]?.has(p.code)
                                ? <Check className="h-4 w-4 inline text-emerald-600 dark:text-emerald-400" />
                                : <span className="text-muted-foreground/40">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {allRoles.map(r => {
              const granted = roleMap[r] ?? new Set<string>();
              return (
                <div key={r} className="rounded-lg border border-border">
                  <div className="px-4 py-2 bg-muted/30 border-b border-border">
                    <div className="font-medium">{ROLE_LABELS[r] ?? r}</div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-mono">{r}</span> · {granted.size} of {totalPerms} permissions
                    </div>
                  </div>
                  <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
                    {cats.map(cat => {
                      const inCat = cat.permissions.filter(p => granted.has(p.code));
                      if (inCat.length === 0) return null;
                      return (
                        <div key={cat.category}>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{cat.category} ({inCat.length})</div>
                          <div className="space-y-0.5">
                            {inCat.map(p => (
                              <div key={p.id} className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                                <Check className="h-3 w-3 text-emerald-600" /> {p.code}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {granted.size === 0 && <div className="text-xs text-muted-foreground italic">No default permissions for this role.</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
